import { AREA_HEIGHT, AREA_WIDTH, getTile, KnyttArea } from '../engine/KnyttArea';
import { KnyttPoint } from '../engine/KnyttPoint';
import { Tileset, TILE_SIZE } from '../render/Tileset';
import { VIEW_H, VIEW_W } from '../render/AreaView';

export type AreaProvider = (p: KnyttPoint) => KnyttArea | null;
export type TilesetProvider = (index: number) => Tileset | null;

/**
 * Pixel-resolution solidity grid covering the current area plus its 8 neighbours,
 * built from each area's collision layer (tile layer index 3). Coordinates are
 * world pixels (areaX*600 + localX, areaY*240 + localY).
 */
export class CollisionMap {
  private grid!: Uint8Array;
  private originX = 0;
  private originY = 0;
  private readonly width = VIEW_W * 3;
  private readonly height = VIEW_H * 3;

  build(center: KnyttPoint, getArea: AreaProvider, getTileset: TilesetProvider): void {
    this.grid = new Uint8Array(this.width * this.height);
    this.originX = (center.x - 1) * VIEW_W;
    this.originY = (center.y - 1) * VIEW_H;

    for (let ay = -1; ay <= 1; ay++) {
      for (let ax = -1; ax <= 1; ax++) {
        const ap = new KnyttPoint(center.x + ax, center.y + ay);
        const area = getArea(ap);
        if (!area || area.empty) continue;
        this.stampArea(area, getTileset);
      }
    }
  }

  private stampArea(area: KnyttArea, getTileset: TilesetProvider): void {
    const layer = area.tileLayers[3];
    if (!layer) return;
    const tsA = getTileset(area.tilesetA);
    const tsB = getTileset(area.tilesetB);
    const baseX = area.position.x * VIEW_W;
    const baseY = area.position.y * VIEW_H;

    for (let ty = 0; ty < AREA_HEIGHT; ty++) {
      for (let tx = 0; tx < AREA_WIDTH; tx++) {
        const b = getTile(layer, tx, ty);
        if (b === 0 || b === 128) continue;
        let ts: Tileset | null;
        let index: number;
        if (b < 128) {
          ts = tsB;
          index = b;
        } else {
          ts = tsA;
          index = b - 128;
        }
        if (!ts) continue;
        const mask = ts.masks[index];
        if (!mask) continue;
        const px0 = baseX + tx * TILE_SIZE;
        const py0 = baseY + ty * TILE_SIZE;
        for (let ly = 0; ly < TILE_SIZE; ly++) {
          const gy = py0 + ly - this.originY;
          if (gy < 0 || gy >= this.height) continue;
          const rowMask = ly * TILE_SIZE;
          const rowGrid = gy * this.width;
          for (let lx = 0; lx < TILE_SIZE; lx++) {
            if (mask[rowMask + lx] === 0) continue;
            const gx = px0 + lx - this.originX;
            if (gx < 0 || gx >= this.width) continue;
            this.grid[rowGrid + gx] = 1;
          }
        }
      }
    }
  }

  /** Mark a world-space rectangle as solid (for dynamic blocks). */
  stampRect(wx: number, wy: number, w: number, h: number): void {
    const x0 = Math.floor(wx) - this.originX;
    const y0 = Math.floor(wy) - this.originY;
    for (let y = y0; y < y0 + h; y++) {
      if (y < 0 || y >= this.height) continue;
      const row = y * this.width;
      for (let x = x0; x < x0 + w; x++) {
        if (x < 0 || x >= this.width) continue;
        this.grid[row + x] = 1;
      }
    }
  }

  /** Clear a solid rect (e.g. a door that just opened). Inverse of stampRect. */
  clearRect(wx: number, wy: number, w: number, h: number): void {
    const x0 = Math.floor(wx) - this.originX;
    const y0 = Math.floor(wy) - this.originY;
    for (let y = y0; y < y0 + h; y++) {
      if (y < 0 || y >= this.height) continue;
      const row = y * this.width;
      for (let x = x0; x < x0 + w; x++) {
        if (x < 0 || x >= this.width) continue;
        this.grid[row + x] = 0;
      }
    }
  }

  /** Is the world pixel (wx,wy) solid? Out-of-range is treated as empty. */
  solidAt(wx: number, wy: number): boolean {
    const gx = (wx | 0) - this.originX;
    const gy = (wy | 0) - this.originY;
    if (gx < 0 || gx >= this.width || gy < 0 || gy >= this.height) return false;
    return this.grid[gy * this.width + gx] !== 0;
  }

  /** Does the axis-aligned box [x,x+w) x [y,y+h) overlap any solid pixel? */
  boxOverlaps(x: number, y: number, w: number, h: number): boolean {
    const x0 = Math.floor(x);
    const y0 = Math.floor(y);
    const x1 = Math.ceil(x + w) - 1;
    const y1 = Math.ceil(y + h) - 1;
    for (let py = y0; py <= y1; py++) {
      for (let px = x0; px <= x1; px++) {
        if (this.solidAt(px, py)) return true;
      }
    }
    return false;
  }
}
